import { supabase } from '../lib/supabase';

interface ImageReference {
  url: string;
  location: string;
  description: string;
}

export async function scanExternalImageUrls(): Promise<ImageReference[]> {
  const externalImages: ImageReference[] = [];
  console.log('Scanning for external images...');

  // Helper to check if URL is external
  const isExternalUrl = (url: string) => {
    if (!url) return false;
    return url.startsWith('http') && !url.includes(import.meta.env.VITE_SUPABASE_URL);
  };

  // Helper to add image if external
  const addIfExternal = (url: string, location: string, description: string) => {
    if (isExternalUrl(url)) {
      externalImages.push({ url, location, description });
    }
  };

  try {
    // Get current settings
    const { data: settingsData } = await supabase
      .from('app_settings')
      .select('settings')
      .single()
      .throwOnError();

    const imageUrls = settingsData?.settings?.media?.imageUrls || {};

    // Add current image URLs to scan
    Object.entries(imageUrls).forEach(([key, url]) => {
      let description = key;
      // Convert momentos_1 to "Momentos Marcantes Image 1"
      if (key.startsWith('momentos_')) {
        const num = key.split('_')[1];
        description = `Momentos Marcantes Image ${num}`;
      } else {
        description = key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      }
      addIfExternal(url as string, 'Project Settings', description);
    });

    // Home page hero images
    addIfExternal(
      'https://images.unsplash.com/photo-1478737270239-2f02b77fc618',
      'Home Page',
      'Hero Background Image 1'
    );
    addIfExternal(
      'https://images.unsplash.com/photo-1516280440614-37939bbacd81',
      'Home Page',
      'Hero Background Image 2'
    );
    addIfExternal(
      'https://images.unsplash.com/photo-1519682337058-a94d519337bc',
      'Home Page',
      'Hero Background Image 3'
    );

    // Project page images
    addIfExternal(
      'https://images.unsplash.com/photo-1522202176988-66273c2fd55f',
      'Project Page',
      'Hero Section Background'
    );

    // Momentos Marcantes section images from WordPress
    addIfExternal(
      'https://wp.sinopseaeaav.pt/wp-content/uploads/2025/03/Imagem-WhatsApp-2025-03-13-as-19.48.01_077350e7.jpg',
      'Project Page',
      'Momentos Marcantes Image 1'
    );
    addIfExternal(
      'https://wp.sinopseaeaav.pt/wp-content/uploads/2025/03/Imagem-WhatsApp-2025-02-24-as-14.00.53_7373e59b.jpg',
      'Project Page',
      'Momentos Marcantes Image 2'
    );
    addIfExternal(
      'https://wp.sinopseaeaav.pt/wp-content/uploads/2025/03/Imagem-WhatsApp-2025-03-15-as-17.37.03_ce0a38f1.jpg',
      'Project Page',
      'Momentos Marcantes Image 3'
    );

    // O que dizem sobre nós section images from Supabase
    addIfExternal(
      `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/general/hugo_carvalho.jpg`,
      'Project Page',
      'Hugo Carvalho Testimonial'
    );
    addIfExternal(
      `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/general/paulo_calhau.jpg`,
      'Project Page',
      'Paulo Calhau Testimonial'
    );
    addIfExternal(
      `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/general/alberico_vieira.jpg`,
      'Project Page',
      'Albérico Vieira Testimonial'
    );
    addIfExternal(
      'https://images.unsplash.com/photo-1478737270239-2f02b77fc618',
      'Project Page',
      'Features Section Image'
    );
    addIfExternal(
      'https://images.unsplash.com/photo-1516280440614-37939bbacd81',
      'Project Page',
      'Gallery Image 1'
    );
    addIfExternal(
      'https://images.unsplash.com/photo-1519682337058-a94d519337bc',
      'Project Page',
      'Gallery Image 2'
    );
    addIfExternal(
      'https://images.unsplash.com/photo-1524178232363-1fb2b075b618',
      'Project Page',
      'Gallery Image 3'
    );
    addIfExternal(
      'https://images.unsplash.com/photo-1516280440614-37939bbacd81',
      'Project Page',
      'Testimonials Background'
    );
    addIfExternal(
      'https://images.unsplash.com/photo-1519682337058-a94d519337bc',
      'Project Page',
      'Call to Action Background'
    );

    // Team page images
    addIfExternal(
      'https://images.unsplash.com/photo-1521737604893-d14cc237f11d',
      'Team Page',
      'Hero Background Image'
    );

    // Courses page images
    addIfExternal(
      'https://images.unsplash.com/photo-1523050854058-8df90110c9f1',
      'Courses Page',
      'Hero Background Image'
    );
    addIfExternal(
      'https://images.unsplash.com/photo-1532094349884-543bc11b234d',
      'Courses Page',
      'Ciências e Tecnologias Image'
    );
    addIfExternal(
      'https://images.unsplash.com/photo-1497633762265-9d179a990aa6',
      'Courses Page',
      'Línguas e Humanidades Image'
    );
    addIfExternal(
      'https://images.unsplash.com/photo-1460925895917-afdab827c52f',
      'Courses Page',
      'Ciências Socioeconómicas Image'
    );
    addIfExternal(
      'https://images.unsplash.com/photo-1452860606245-08befc0ff44b',
      'Courses Page',
      'Artes Visuais Image'
    );

    // Professional Courses images
    addIfExternal(
      'https://images.unsplash.com/photo-1542744094-3a31f272c490',
      'Professional Courses',
      'Multimedia Course Image'
    );
    addIfExternal(
      'https://images.unsplash.com/photo-1555066931-4365d14bab8c',
      'Professional Courses',
      'Programming Course Image'
    );
    addIfExternal(
      'https://images.unsplash.com/photo-1581092921461-eab62e97a780',
      'Professional Courses',
      'Industrial Maintenance Course Image'
    );
    addIfExternal(
      'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6',
      'Professional Courses',
      'Commercial Course Image'
    );
    addIfExternal(
      'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d',
      'Professional Courses',
      'Health Assistant Course Image'
    );
    addIfExternal(
      'https://images.unsplash.com/photo-1448375240586-882707db888b',
      'Professional Courses',
      'Forestry Course Image'
    );

    // News page images
    addIfExternal(
      'https://images.unsplash.com/photo-1557682250-33bd709cbe85',
      'News Page',
      'Hero Background Image'
    );

    // Podcasts page images
    addIfExternal(
      'https://images.unsplash.com/photo-1478737270239-2f02b77fc618',
      'Podcasts Page',
      'Hero Background Image'
    );

    return externalImages;
  } catch (error) {
    console.error('Error scanning for external images:', error);
    throw error;
  }
}

export async function generateImageReport(): Promise<string> {
  try {
    const images = await scanExternalImageUrls();
    
    let report = '# External Image URLs Report\n\n';
    
    // Group by location
    const groupedImages = images.reduce((acc, img) => {
      if (!acc[img.location]) {
        acc[img.location] = [];
      }
      acc[img.location].push(img);
      return acc;
    }, {} as { [key: string]: ImageReference[] });

    // Generate report sections
    Object.entries(groupedImages).forEach(([location, images]) => {
      report += `## ${location}\n\n`;
      images.forEach(img => {
        report += `- ${img.description}\n`;
        report += `  URL: ${img.url}\n\n`;
      });
    });

    return report;
  } catch (error) {
    console.error('Error generating image report:', error);
    return '# Error generating report\n\nAn error occurred while generating the image report.';
  }
}